import React, { useRef, useState, useEffect } from 'react';
import { MagnifyingGlass, ArrowRight, Image } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

import styles from './FollowUpSearchBar.module.css';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import type { TextConfig, AttachedFile } from './types';
import { AttachmentThumbnails } from './AttachmentThumbnails';

import { cn } from '@/lib/utils';

interface FollowUpSearchBarProps {
  initialQuery?: string;
  onQueryChange: (query: string) => void;
  isSearching: boolean;
  handleFollowUpSearch: (query: string) => void;
  textConfig: TextConfig;
  enableFileAttachment?: boolean;
  attachedFiles?: AttachedFile[];
  isUploading?: boolean;
  onAddFiles?: (files: File[]) => void;
  onRemoveFile?: (id: string) => void;
  disabled?: boolean;
}

export const FollowUpSearchBar = React.forwardRef<HTMLTextAreaElement, FollowUpSearchBarProps>(
  (
    {
      initialQuery = '',
      onQueryChange,
      isSearching,
      handleFollowUpSearch,
      textConfig,
      enableFileAttachment = false,
      attachedFiles = [],
      isUploading = false,
      onAddFiles,
      onRemoveFile,
      disabled = false,
    },
    ref,
  ) => {
    // Use internal state instead of prop
    const [localQuery, setLocalQuery] = useState(initialQuery);
    const [isDragOver, setIsDragOver] = useState(false);

    const followUpInputRef = useRef<HTMLTextAreaElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const setCombinedRef = (instance: HTMLTextAreaElement | null) => {
      followUpInputRef.current = instance;
      if (typeof ref === 'function') {
        ref(instance);
      } else if (ref && 'current' in ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = instance;
      }
    };

    // Automatically adjust height based on content
    useEffect(() => {
      if (followUpInputRef.current) {
        followUpInputRef.current.style.height = 'inherit';
        followUpInputRef.current.style.height = `${followUpInputRef.current.scrollHeight}px`;
      }
    }, [localQuery]);

    // Reset local state when initialQuery changes (e.g., after search is performed)
    useEffect(() => {
      setLocalQuery(initialQuery);
    }, [initialQuery]);

    const isSendDisabled = disabled || isSearching || !localQuery.trim() || isUploading;

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !isSendDisabled) {
        e.preventDefault();
        const currentQuery = localQuery;
        onQueryChange(currentQuery); // Emit the query change to parent
        handleFollowUpSearch(currentQuery);
        // Clear the local state immediately
        setLocalQuery('');
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalQuery(e.target.value);
    };

    const handleBlur = () => {
      if (localQuery !== initialQuery) {
        onQueryChange(localQuery); // Emit the query change to parent on blur
      }
    };

    const handleSearchClick = () => {
      const currentQuery = localQuery;
      onQueryChange(currentQuery); // Emit the query change to parent
      handleFollowUpSearch(currentQuery);
      // Clear the local state immediately
      setLocalQuery('');
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      if (!enableFileAttachment || !onAddFiles) return;
      const files = Array.from(e.clipboardData.files);
      if (files.length > 0) {
        e.preventDefault();
        onAddFiles(files);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!enableFileAttachment || !onAddFiles) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onAddFiles(files);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      if (enableFileAttachment) setIsDragOver(true);
    };

    const handleDragLeave = () => {
      setIsDragOver(false);
    };

    return (
      <motion.footer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={styles.followUpSearchBar}
      >
        <div className="container flex items-center justify-center p-3">
          <div
            className={cn(
              'relative flex w-full flex-col rounded bg-card border border-dark-400',
              isDragOver && enableFileAttachment && 'ring-2 ring-primary',
            )}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onPaste={handlePaste}
          >
            {isDragOver && enableFileAttachment && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center rounded"
                style={{ backgroundColor: 'color-mix(in srgb, var(--bfc-card) 90%, transparent)' }}
              >
                <p className="text-sm font-medium text-muted-foreground">Drop images here to attach</p>
              </div>
            )}

            {/* Attached files */}
            {enableFileAttachment && (
              <AttachmentThumbnails
                items={attachedFiles.map((f) => ({ id: f.id, src: f.preview, url: f.publicUrl, status: f.status }))}
                size="sm"
                onRemove={onRemoveFile}
                className="pt-2 px-2"
              />
            )}

            <div className="flex items-center gap-2 px-2">
              {enableFileAttachment && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) {
                        onAddFiles?.(Array.from(e.target.files));
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-md size-7 hover:bg-opacity-80 disabled:opacity-50 shrink-0"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || attachedFiles.length >= 5}
                    aria-label={textConfig.attachImageText}
                  >
                    <Image weight="regular" className="size-4 text-secondary-foreground" />
                  </Button>
                </>
              )}
              {!enableFileAttachment && (
                <div className="size-6 flex flex-shrink-0 justify-center items-center">
                  <MagnifyingGlass className="size-5 text-secondary-foreground" />
                </div>
              )}
              <Textarea
                ref={setCombinedRef}
                value={localQuery}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyPress}
                placeholder={textConfig.followUpPlaceholder}
                disabled={disabled}
                className="min-h-11 w-full resize-none border-0 shadow-none bg-transparent px-0 py-2.5 focus:outline-none focus:ring-0 focus-visible:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                rows={1}
              />
              <Button
                type="button"
                size="icon"
                disabled={isSendDisabled}
                onClick={handleSearchClick}
                className={cn(
                  'size-8 shrink-0 rounded',
                  localQuery ? 'bg-primary text-primary-foreground' : 'bg-dark-400 text-white',
                )}
              >
                {isSearching ? (
                  <div className="animate-spin rounded-full size-4 sm:size-5 border-2 border-dark/40 !border-t-foreground" />
                ) : (
                  <ArrowRight className="size-6" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.footer>
    );
  },
);

FollowUpSearchBar.displayName = 'FollowUpSearchBar';
